// customer/src/pages/CourierPage.jsx
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Package, FileText, Gift, Weight,
  MapPin, Phone, User, CheckCircle,
  Loader2, Truck, X, Search, Home as HomeIcon,
  ShoppingCart, ListOrdered, AlertCircle, RefreshCw,
  Navigation, Bike, Box, Edit3,
  Wallet, Smartphone, Copy, Clock, CheckCircle2, Banknote,
  Send, Shield, Zap, ChevronRight, Star,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = typeof window !== "undefined" && window.location.hostname !== "localhost"
  ? "https://quickry-ivrv.onrender.com/api" : "http://localhost:5000/api";

/* ── Design Tokens ──────────────────────────────── */
const T = {
  // Backgrounds
  bg:       "#0a0a0f",
  surface:  "#111118",
  card:     "#16161f",
  cardHov:  "#1c1c28",
  border:   "#1f1f2e",
  borderHi: "#2a2a40",

  // Text
  text:     "#f0f0f8",
  sub:      "#8888aa",
  muted:    "#44445a",

  // Accents
  blue:     "#4f8ef7",
  blueDark: "#1a3a8a",
  blueGlow: "rgba(79,142,247,0.15)",
  green:    "#22d37a",
  greenDim: "rgba(34,211,122,0.12)",
  red:      "#f25c5c",
  redDim:   "rgba(242,92,92,0.1)",
  orange:   "#f97316",
  purple:   "#9b6dff",
  purpleDim:"rgba(155,109,255,0.1)",

  // UI
  white:    "#ffffff",
  navBg:    "rgba(10,10,15,0.95)",
};

const SIZES = [
  { type:"document", label:"Document",   sub:"Upto 500g",  price:49,  Icon:FileText, color:T.blue,   bg:"rgba(79,142,247,0.08)"  },
  { type:"small",    label:"Small Box",  sub:"Upto 2kg",   price:79,  Icon:Box,      color:T.green,  bg:"rgba(34,211,122,0.08)"  },
  { type:"medium",   label:"Medium",     sub:"Upto 5kg",   price:119, Icon:Gift,     color:T.purple, bg:"rgba(155,109,255,0.08)" },
  { type:"heavy",    label:"Heavy",      sub:"5kg+",       price:179, Icon:Weight,   color:T.red,    bg:"rgba(242,92,92,0.08)"   },
];

const STATUS_STEPS = [
  { status:"placed",          label:"Booking Placed",   Icon:CheckCircle },
  { status:"pickup_assigned", label:"Rider Assigned",   Icon:Bike        },
  { status:"picked_up",       label:"Parcel Picked Up", Icon:Package     },
  { status:"in_transit",      label:"In Transit",       Icon:Truck       },
  { status:"delivered",       label:"Delivered",        Icon:CheckCircle },
];

const NAV = [
  { icon:HomeIcon,     label:"Home",    route:"/"            },
  { icon:Search,       label:"Explore", route:"/restaurants" },
  { icon:ShoppingCart, label:"Cart",    route:"/cart"        },
  { icon:ListOrdered,  label:"Orders",  route:"/orders"      },
  { icon:User,         label:"Profile", route:"/profile"     },
];

const UPI_ID   = "quickry@upi";
const UPI_NAME = "Quickry";

/* ── Global Styles ──────────────────────────────── */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  button,input,textarea,select{font-family:inherit;outline:none}
  ::-webkit-scrollbar{display:none}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}
  @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
  @keyframes glow{0%,100%{box-shadow:0 0 20px rgba(79,142,247,0.2)}50%{box-shadow:0 0 40px rgba(79,142,247,0.5)}}
  .fade-up{animation:fadeUp .35s ease both}
  .spin{animation:spin 1s linear infinite}
  input::placeholder{color:#44445a}
  input:focus{border-color:#4f8ef7 !important}
`;

/* ── Address Form ──────────────────────────────── */
function AddressForm({ data, onChange, errors }) {
  const fields = [
    { key:"name",     label:"Full Name",         placeholder:"Sender / Receiver naam",  icon:User      },
    { key:"phone",    label:"Mobile Number",      placeholder:"10-digit number",         icon:Phone     },
    { key:"address",  label:"Complete Address",   placeholder:"House no, Street, Area",  icon:MapPin    },
    { key:"landmark", label:"Landmark (Optional)",placeholder:"Near SBI Bank",           icon:Navigation},
    { key:"pincode",  label:"Pincode",            placeholder:"e.g. 444001",             icon:MapPin    },
    { key:"city",     label:"City",               placeholder:"e.g. Akola",              icon:MapPin    },
  ];
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      {fields.map(f => (
        <div key={f.key}>
          <div style={{ fontSize:"0.62rem", fontWeight:700, color:T.sub, marginBottom:5, textTransform:"uppercase", letterSpacing:"0.8px" }}>{f.label}</div>
          <div style={{
            display:"flex", alignItems:"center", gap:10,
            border:"1.5px solid " + (errors?.[f.key] ? T.red : data[f.key] ? T.blue : T.border),
            borderRadius:12, padding:"11px 14px", background:T.surface,
            transition:"border-color .2s",
          }}>
            <f.icon size={14} color={data[f.key] ? T.blue : T.muted}/>
            <input
              value={data[f.key] || ""}
              onChange={e => onChange(f.key, e.target.value)}
              placeholder={f.placeholder}
              style={{ flex:1, border:"none", background:"transparent", fontSize:"0.85rem", color:T.text, fontFamily:"inherit" }}
            />
          </div>
          {errors?.[f.key] && (
            <div style={{ fontSize:"0.65rem", color:T.red, marginTop:4, fontWeight:600, display:"flex", alignItems:"center", gap:4 }}>
              <AlertCircle size={10}/> {errors[f.key]}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Saved Address Sheet ──────────────────────── */
function AddressPicker({ addresses, onSelect, onClose }) {
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:500, display:"flex", alignItems:"flex-end", backdropFilter:"blur(6px)" }}>
      <div onClick={e => e.stopPropagation()} style={{ width:"100%", background:T.card, borderRadius:"22px 22px 0 0", padding:"20px 16px", maxHeight:"70vh", overflowY:"auto", border:"1px solid " + T.border }}>
        <div style={{ width:36, height:3, borderRadius:100, background:T.border, margin:"0 auto 18px" }}/>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:"1rem", color:T.text }}>Saved Addresses</div>
          <button onClick={onClose} style={{ background:T.surface, border:"1px solid " + T.border, borderRadius:8, width:30, height:30, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
            <X size={14} color={T.sub}/>
          </button>
        </div>
        {addresses.length === 0 ? (
          <div style={{ textAlign:"center", padding:"30px 0", color:T.muted, fontSize:"0.83rem" }}>Koi saved address nahi</div>
        ) : addresses.map((addr, i) => (
          <div key={i} onClick={() => onSelect(addr)}
            style={{ background:T.surface, borderRadius:12, padding:"13px 14px", marginBottom:10, cursor:"pointer", border:"1.5px solid " + T.border }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
              <MapPin size={13} color={T.blue}/>
              <span style={{ fontWeight:700, fontSize:"0.83rem", color:T.text, textTransform:"capitalize" }}>
                {addr.label || addr.type || "Address " + (i+1)}
              </span>
            </div>
            <div style={{ fontSize:"0.75rem", color:T.sub, lineHeight:1.6 }}>
              {addr.address || addr.fullAddress}
              {addr.city ? ", " + addr.city : ""}
              {addr.pincode ? " - " + addr.pincode : ""}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Booking Confirmed ──────────────────────────── */
function BookingConfirmed({ booking, onGoHome, onTrack }) {
  return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:28, fontFamily:"'DM Sans',sans-serif" }}>
      <style>{GLOBAL_CSS}</style>
      <div className="fade-up" style={{ textAlign:"center" }}>
        {/* Icon ring */}
        <div style={{ width:96, height:96, borderRadius:"50%", background:T.greenDim, border:"2px solid " + T.green, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 24px", boxShadow:"0 0 40px rgba(34,211,122,0.3)" }}>
          <CheckCircle size={46} color={T.green} strokeWidth={2}/>
        </div>
        <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:"1.8rem", color:T.text, marginBottom:6 }}>Booking Confirmed</div>
        <div style={{ color:T.sub, fontSize:"0.83rem", marginBottom:32, lineHeight:1.7 }}>Parcel pickup ke liye schedule ho gaya hai</div>

        {/* Tracking ID card */}
        <div style={{ background:"linear-gradient(135deg,#0d1a3a,#1a3a8a)", border:"1px solid " + T.blue + "44", borderRadius:18, padding:"20px 24px", marginBottom:16, width:"100%", maxWidth:340, animation:"glow 3s ease-in-out infinite" }}>
          <div style={{ fontSize:"0.6rem", fontWeight:700, color:T.blue, letterSpacing:"1.5px", textTransform:"uppercase", marginBottom:8 }}>Tracking ID</div>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:"1.5rem", color:T.text, letterSpacing:"3px" }}>{booking.trackingId}</div>
          <div style={{ fontSize:"0.68rem", color:T.sub, marginTop:5 }}>Ise save kar lo — track karne ke liye zaroori hai</div>
        </div>

        {/* Details */}
        <div style={{ background:T.card, border:"1px solid " + T.border, borderRadius:14, padding:"16px", width:"100%", maxWidth:340, marginBottom:24 }}>
          {[
            { label:"Parcel Type", value: booking.parcelType?.charAt(0).toUpperCase() + booking.parcelType?.slice(1) },
            { label:"Amount",      value: "₹" + booking.price },
            { label:"Payment",     value: booking.paymentMode?.toUpperCase() },
          ].map((r,i) => (
            <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom: i<2 ? "1px solid " + T.border : "none" }}>
              <span style={{ fontSize:"0.78rem", color:T.sub }}>{r.label}</span>
              <span style={{ fontSize:"0.82rem", fontWeight:700, color:T.text }}>{r.value}</span>
            </div>
          ))}
        </div>

        <div style={{ display:"flex", gap:10, width:"100%", maxWidth:340 }}>
          <button onClick={onTrack} style={{ flex:1, background:"linear-gradient(135deg,#1a3a8a,#4f8ef7)", color:"#fff", border:"none", borderRadius:12, padding:"13px", fontWeight:700, fontSize:"0.88rem", cursor:"pointer" }}>
            Track Parcel
          </button>
          <button onClick={onGoHome} style={{ flex:1, background:T.card, color:T.text, border:"1px solid " + T.border, borderRadius:12, padding:"13px", fontWeight:700, fontSize:"0.88rem", cursor:"pointer" }}>
            Home
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   WALLET MODAL — Full Premium Dark
════════════════════════════════════════════════════ */
function WalletModal({ balance, onClose, token, onBalanceUpdate }) {
  const [tab,        setTab]        = useState("overview");
  const [amount,     setAmount]     = useState("");
  const [utr,        setUtr]        = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success,    setSuccess]    = useState(false);
  const [error,      setError]      = useState("");
  const [txns,       setTxns]       = useState([]);
  const [loadTxn,    setLoadTxn]    = useState(false);
  const [copied,     setCopied]     = useState(false);

  const QUICK_AMOUNTS = [50, 100, 200, 500];

  const fetchTxns = async () => {
    setLoadTxn(true);
    try {
      const res  = await fetch(API + "/wallet/transactions", { headers:{ Authorization:"Bearer " + token } });
      const data = await res.json();
      if (data.success) setTxns(data.transactions || []);
    } catch {}
    setLoadTxn(false);
  };

  useEffect(() => { if (tab === "history") fetchTxns(); }, [tab]);

  const copyUPI = () => {
    navigator.clipboard?.writeText(UPI_ID).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const submitDeposit = async () => {
    setError("");
    if (!amount || isNaN(amount) || Number(amount) < 10) { setError("Minimum ₹10 add karo"); return; }
    if (!utr.trim() || utr.trim().length < 6)            { setError("Valid UTR / Transaction ID daalo"); return; }
    setSubmitting(true);
    try {
      const res  = await fetch(API + "/wallet/deposit-request", {
        method:  "POST",
        headers: { "Content-Type":"application/json", Authorization:"Bearer " + token },
        body:    JSON.stringify({ amount: Number(amount), utr: utr.trim() }),
      });
      const data = await res.json();
      if (data.success) { setSuccess(true); }
      else setError(data.message || "Request submit nahi hua");
    } catch { setError("Server error. Try again."); }
    setSubmitting(false);
  };

  return (
    <div onClick={onClose}
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:600, display:"flex", alignItems:"flex-end", backdropFilter:"blur(8px)" }}>
      <div onClick={e => e.stopPropagation()}
        style={{
          background:T.card, borderRadius:"24px 24px 0 0", width:"100%", maxWidth:560,
          margin:"0 auto", maxHeight:"92vh", overflowY:"auto",
          border:"1px solid " + T.borderHi, borderBottom:"none",
          fontFamily:"'DM Sans',system-ui,sans-serif",
        }}>

        {/* Handle */}
        <div style={{ width:36, height:3, borderRadius:100, background:T.border, margin:"14px auto 0" }}/>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 20px 0" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:44, height:44, borderRadius:14, background:T.greenDim, border:"1px solid " + T.green + "44", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Wallet size={20} color={T.green}/>
            </div>
            <div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:"1rem", color:T.text }}>Quickry Wallet</div>
              <div style={{ fontSize:"0.62rem", color:T.sub, marginTop:1 }}>Secure · Instant · Easy</div>
            </div>
          </div>
          <button onClick={onClose}
            style={{ background:T.surface, border:"1px solid " + T.border, borderRadius:10, width:34, height:34, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
            <X size={15} color={T.sub}/>
          </button>
        </div>

        {/* Balance Banner */}
        <div style={{ margin:"16px 20px", background:"linear-gradient(135deg,#061a0e,#0b2e18,#0f3d1e)", border:"1px solid rgba(34,211,122,0.2)", borderRadius:18, padding:"20px 22px", position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", right:-10, top:-10, opacity:0.04 }}>
            <Wallet size={120} color={T.green} strokeWidth={1}/>
          </div>
          <div style={{ fontSize:"0.6rem", fontWeight:700, color:T.green, letterSpacing:"1.5px", textTransform:"uppercase", marginBottom:8 }}>Available Balance</div>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:"2.4rem", color:T.text, letterSpacing:"-1px" }}>₹{balance}</div>
          <div style={{ fontSize:"0.7rem", color:"rgba(34,211,122,0.6)", marginTop:4 }}>Courier booking mein use karo</div>
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", margin:"0 20px 18px", background:T.surface, borderRadius:12, padding:3, border:"1px solid " + T.border }}>
          {[{ id:"overview", label:"Overview" }, { id:"topup", label:"Add Money" }, { id:"history", label:"History" }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{
                flex:1, padding:"9px 8px", borderRadius:10, border:"none",
                background:tab===t.id ? T.card : "transparent",
                color:tab===t.id ? T.text : T.sub,
                fontWeight:tab===t.id ? 700 : 400,
                fontSize:"0.77rem", cursor:"pointer",
                fontFamily:"inherit", transition:"all .2s",
                boxShadow: tab===t.id ? "0 1px 8px rgba(0,0,0,0.3)" : "none",
              }}>
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ padding:"0 20px 48px" }}>

          {/* ── OVERVIEW ── */}
          {tab === "overview" && (
            <div className="fade-up">
              {/* How it works */}
              <div style={{ background:T.surface, border:"1px solid " + T.border, borderRadius:16, padding:"18px", marginBottom:14 }}>
                <div style={{ fontSize:"0.62rem", fontWeight:700, color:T.sub, letterSpacing:"1px", textTransform:"uppercase", marginBottom:14 }}>How it works</div>
                {[
                  { step:"01", text:"GPay / PhonePe se UPI payment karo",    icon:Smartphone, color:T.blue   },
                  { step:"02", text:"UTR number yahan enter karo",             icon:Copy,       color:T.purple },
                  { step:"03", text:"Admin verify karega — 10 min mein",       icon:Shield,     color:T.orange },
                  { step:"04", text:"Wallet mein amount credit ho jayega",     icon:CheckCircle2, color:T.green },
                ].map((s,i) => (
                  <div key={s.step} style={{ display:"flex", alignItems:"center", gap:14, marginBottom:i<3?14:0 }}>
                    <div style={{ width:36, height:36, borderRadius:10, background:s.color+"15", border:"1px solid " + s.color+"30", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      <s.icon size={16} color={s.color}/>
                    </div>
                    <div style={{ flex:1 }}>
                      <span style={{ fontSize:"0.6rem", fontWeight:700, color:T.muted, marginRight:8 }}>{s.step}</span>
                      <span style={{ fontSize:"0.82rem", color:T.sub }}>{s.text}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Security note */}
              <div style={{ background:"rgba(79,142,247,0.05)", border:"1px solid rgba(79,142,247,0.15)", borderRadius:12, padding:"12px 14px", display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
                <Shield size={15} color={T.blue}/>
                <span style={{ fontSize:"0.75rem", color:T.sub, lineHeight:1.5 }}>
                  Har transaction manually verify hota hai. Sirf admin approved payment wallet mein credit hoga.
                </span>
              </div>

              <button onClick={() => setTab("topup")}
                style={{ width:"100%", background:"linear-gradient(135deg,#0b2e18,#22d37a)", border:"none", borderRadius:14, padding:"14px", fontWeight:700, fontSize:"0.9rem", color:"#000", cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                <Zap size={16}/> Add Money to Wallet
              </button>
            </div>
          )}

          {/* ── ADD MONEY ── */}
          {tab === "topup" && (
            <div className="fade-up">
              {success ? (
                <div style={{ textAlign:"center", padding:"30px 0" }}>
                  <div style={{ width:72, height:72, borderRadius:"50%", background:T.greenDim, border:"2px solid " + T.green, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 18px", boxShadow:"0 0 30px rgba(34,211,122,0.3)" }}>
                    <CheckCircle2 size={34} color={T.green}/>
                  </div>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:"1.2rem", color:T.text, marginBottom:8 }}>Request Submitted!</div>
                  <div style={{ fontSize:"0.82rem", color:T.sub, lineHeight:1.7, marginBottom:24 }}>
                    Admin verify karega.<br/>10 minutes mein wallet mein credit hoga.
                  </div>
                  <button onClick={() => { setSuccess(false); setAmount(""); setUtr(""); }}
                    style={{ background:T.card, border:"1px solid " + T.border, borderRadius:12, padding:"11px 24px", color:T.text, fontWeight:700, fontSize:"0.85rem", cursor:"pointer", fontFamily:"inherit" }}>
                    Naya Request Karo
                  </button>
                </div>
              ) : (
                <>
                  {/* Step 1 — UPI QR */}
                  <div style={{ background:T.surface, border:"1px solid " + T.border, borderRadius:16, padding:"18px", marginBottom:14 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
                      <div style={{ width:24, height:24, borderRadius:"50%", background:T.blueGlow, border:"1px solid " + T.blue + "50", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.65rem", fontWeight:800, color:T.blue }}>1</div>
                      <span style={{ fontWeight:700, fontSize:"0.85rem", color:T.text }}>UPI se Payment Karo</span>
                    </div>

                    {/* QR Placeholder */}
                    <div style={{ background:"#fff", borderRadius:12, padding:12, width:120, height:120, margin:"0 auto 14px", display:"flex", alignItems:"center", justifyContent:"center" }}>
                      <div style={{ textAlign:"center" }}>
                        <div style={{ fontSize:"0.55rem", color:"#888", fontWeight:600 }}>QR Code</div>
                        <div style={{ fontSize:"0.5rem", color:"#aaa", marginTop:4 }}>GPay / PhonePe</div>
                      </div>
                    </div>

                    {/* UPI ID copy */}
                    <div style={{ background:T.card, border:"1px solid " + T.borderHi, borderRadius:10, padding:"10px 14px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <div>
                        <div style={{ fontSize:"0.6rem", color:T.sub, marginBottom:2 }}>UPI ID</div>
                        <div style={{ fontWeight:700, fontSize:"0.88rem", color:T.blue, letterSpacing:"0.5px" }}>{UPI_ID}</div>
                      </div>
                      <button onClick={copyUPI}
                        style={{ background:copied ? T.greenDim : T.blueGlow, border:"1px solid " + (copied ? T.green : T.blue) + "44", borderRadius:8, padding:"7px 12px", color:copied ? T.green : T.blue, fontWeight:700, fontSize:"0.72rem", cursor:"pointer", display:"flex", alignItems:"center", gap:5, fontFamily:"inherit", transition:"all .2s" }}>
                        {copied ? <CheckCircle2 size={13}/> : <Copy size={13}/>}
                        {copied ? "Copied!" : "Copy"}
                      </button>
                    </div>
                  </div>

                  {/* Step 2 — Amount + UTR */}
                  <div style={{ background:T.surface, border:"1px solid " + T.border, borderRadius:16, padding:"18px", marginBottom:14 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
                      <div style={{ width:24, height:24, borderRadius:"50%", background:T.blueGlow, border:"1px solid " + T.blue + "50", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.65rem", fontWeight:800, color:T.blue }}>2</div>
                      <span style={{ fontWeight:700, fontSize:"0.85rem", color:T.text }}>Amount Daalo</span>
                    </div>

                    {/* Quick amounts */}
                    <div style={{ display:"flex", gap:8, marginBottom:12 }}>
                      {QUICK_AMOUNTS.map(a => (
                        <button key={a} onClick={() => setAmount(String(a))}
                          style={{ flex:1, padding:"8px 4px", borderRadius:8, border:"1px solid " + (amount===String(a) ? T.blue : T.border), background:amount===String(a) ? T.blueGlow : "transparent", color:amount===String(a) ? T.blue : T.sub, fontWeight:700, fontSize:"0.72rem", cursor:"pointer", fontFamily:"inherit", transition:"all .15s" }}>
                          ₹{a}
                        </button>
                      ))}
                    </div>

                    <input
                      type="number" value={amount} onChange={e => setAmount(e.target.value)}
                      placeholder="Ya custom amount daalo (min ₹10)"
                      style={{ width:"100%", border:"1.5px solid " + (amount ? T.blue : T.border), borderRadius:10, background:T.card, padding:"11px 14px", fontSize:"0.88rem", color:T.text, fontFamily:"inherit", transition:"border-color .2s" }}
                    />
                  </div>

                  <div style={{ background:T.surface, border:"1px solid " + T.border, borderRadius:16, padding:"18px", marginBottom:14 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                      <div style={{ width:24, height:24, borderRadius:"50%", background:T.blueGlow, border:"1px solid " + T.blue + "50", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.65rem", fontWeight:800, color:T.blue }}>3</div>
                      <span style={{ fontWeight:700, fontSize:"0.85rem", color:T.text }}>UTR / Transaction ID Daalo</span>
                    </div>

                    <input
                      type="text" value={utr} onChange={e => setUtr(e.target.value)}
                      placeholder="e.g. 407123456789"
                      style={{ width:"100%", border:"1.5px solid " + (utr ? T.blue : T.border), borderRadius:10, background:T.card, padding:"11px 14px", fontSize:"0.88rem", color:T.text, fontFamily:"inherit", transition:"border-color .2s", marginBottom:8 }}
                    />
                    <div style={{ fontSize:"0.67rem", color:T.muted, display:"flex", alignItems:"center", gap:5 }}>
                      <AlertCircle size={11} color={T.muted}/>
                      GPay app → Payment history → Transaction details mein milta hai
                    </div>
                  </div>

                  {error && (
                    <div style={{ background:T.redDim, border:"1px solid " + T.red + "40", borderRadius:10, padding:"10px 14px", marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
                      <AlertCircle size={13} color={T.red}/>
                      <span style={{ fontSize:"0.8rem", color:T.red, fontWeight:600 }}>{error}</span>
                    </div>
                  )}

                  <button onClick={submitDeposit} disabled={submitting}
                    style={{ width:"100%", background:submitting ? T.surface : "linear-gradient(135deg,#0b2e18,#22d37a)", border:"1px solid " + (submitting ? T.border : "transparent"), borderRadius:14, padding:"14px", fontWeight:700, fontSize:"0.9rem", color:submitting ? T.sub : "#000", cursor:submitting?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, fontFamily:"inherit", transition:"all .2s" }}>
                    {submitting
                      ? <><Loader2 size={17} className="spin"/> Submitting...</>
                      : <><Send size={16}/> Submit Deposit Request</>}
                  </button>

                  <div style={{ textAlign:"center", marginTop:12, fontSize:"0.68rem", color:T.muted, lineHeight:1.8 }}>
                    Admin 10 minutes mein approve karega<br/>
                    Approved hone par wallet mein dikhega
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── HISTORY ── */}
          {tab === "history" && (
            <div className="fade-up">
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:"0.9rem", color:T.text }}>Transaction History</div>
                <button onClick={fetchTxns} style={{ background:"none", border:"none", cursor:"pointer", color:T.sub, display:"flex", alignItems:"center", gap:5, fontSize:"0.75rem" }}>
                  <RefreshCw size={13} style={{ animation:loadTxn ? "spin 1s linear infinite" : "none" }}/> Refresh
                </button>
              </div>
              {loadTxn ? (
                <div style={{ textAlign:"center", padding:40 }}>
                  <Loader2 size={28} color={T.green} className="spin"/>
                </div>
              ) : txns.length === 0 ? (
                <div style={{ textAlign:"center", padding:"40px 0", color:T.muted, fontSize:"0.82rem" }}>
                  Koi transactions nahi abhi tak
                </div>
              ) : txns.map((t,i) => {
                const isCredit  = t.type === "credit";
                const isPending = t.status === "pending";
                const statusColor = isPending ? T.orange : isCredit ? T.green : T.red;
                return (
                  <div key={t._id||i} style={{ background:T.surface, border:"1px solid " + T.border, borderRadius:14, padding:"14px 16px", marginBottom:10 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                          <div style={{ width:28, height:28, borderRadius:"50%", background:statusColor+"15", display:"flex", alignItems:"center", justifyContent:"center" }}>
                            {isCredit ? <Banknote size={13} color={statusColor}/> : <Send size={13} color={statusColor}/>}
                          </div>
                          <span style={{ fontWeight:700, fontSize:"0.83rem", color:T.text }}>
                            {t.description || (isCredit ? "Wallet Credit" : "Wallet Debit")}
                          </span>
                        </div>
                        {t.utr && <div style={{ fontSize:"0.65rem", color:T.muted, paddingLeft:36 }}>UTR: {t.utr}</div>}
                        <div style={{ fontSize:"0.62rem", color:T.muted, marginTop:3, paddingLeft:36 }}>
                          {new Date(t.createdAt).toLocaleString("en-IN")}
                        </div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:"0.95rem", color:isCredit ? T.green : T.red }}>
                          {isCredit ? "+" : "-"}₹{t.amount}
                        </div>
                        <div style={{ fontSize:"0.6rem", fontWeight:700, marginTop:4, padding:"2px 8px", borderRadius:100, background:statusColor+"15", color:statusColor }}>
                          {isPending ? "PENDING" : t.status?.toUpperCase()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════════ */
export default function CourierPage() {
  const navigate = useNavigate();
  const { isLoggedIn, getToken, user } = useAuth();

  const [step,      setStep]      = useState("home");
  const [selected,  setSelected]  = useState(null);

  const emptyAddr = { name:"", phone:"", address:"", landmark:"", pincode:"", city:"Akola" };
  const [pickup,    setPickup]    = useState({ ...emptyAddr });
  const [delivery,  setDelivery]  = useState({ ...emptyAddr });
  const [payment,   setPayment]   = useState("cod");
  const [desc,      setDesc]      = useState("");
  const [fragile,   setFragile]   = useState(false);
  const [errors,    setErrors]    = useState({});
  const [placing,   setPlacing]   = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const [showPickupSheet,   setShowPickupSheet]   = useState(false);
  const [showDeliverySheet, setShowDeliverySheet] = useState(false);

  const [trackId,   setTrackId]   = useState("");
  const [tracking,  setTracking]  = useState(null);
  const [trackErr,  setTrackErr]  = useState("");
  const [tracking_, setTracking_] = useState(false);

  const [history,   setHistory]   = useState([]);
  const [loadHist,  setLoadHist]  = useState(false);

  const [walletBal,  setWalletBal]  = useState(0);
  const [showWallet, setShowWallet] = useState(false);

  /* Auto-fill pickup from user profile */
  useEffect(() => {
    if (user) {
      const a = user.addresses?.[0];
      setPickup(p => ({
        ...p,
        name:     p.name     || user.name  || "",
        phone:    p.phone    || user.phone || "",
        address:  p.address  || a?.address || a?.fullAddress || "",
        landmark: p.landmark || a?.landmark || "",
        pincode:  p.pincode  || a?.pincode  || "",
        city:     p.city     || a?.city     || "Akola",
      }));
    }
  }, [user]);

  const applyToPickup = (addr) => {
    setPickup(p => ({ ...p, address:addr.address||addr.fullAddress||"", landmark:addr.landmark||"", pincode:addr.pincode||"", city:addr.city||"Akola" }));
    setShowPickupSheet(false);
  };
  const applyToDelivery = (addr) => {
    setDelivery(d => ({ ...d, address:addr.address||addr.fullAddress||"", landmark:addr.landmark||"", pincode:addr.pincode||"", city:addr.city||"Akola" }));
    setShowDeliverySheet(false);
  };

  const validate = () => {
    const e = {};
    if (!selected)         e.size        = "Parcel size select karo";
    if (!pickup.name)      e.pickupName  = "Name required";
    if (!pickup.phone)     e.pickupPhone = "Phone required";
    if (!pickup.address)   e.pickupAddr  = "Address required";
    if (!pickup.pincode)   e.pickupPin   = "Pincode required";
    if (!pickup.city)      e.pickupCity  = "City required";
    if (!delivery.name)    e.delivName   = "Name required";
    if (!delivery.phone)   e.delivPhone  = "Phone required";
    if (!delivery.address) e.delivAddr   = "Address required";
    if (!delivery.pincode) e.delivPin    = "Pincode required";
    if (!delivery.city)    e.delivCity   = "City required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const placeBooking = async () => {
    if (!isLoggedIn) { navigate("/login", { state:{ from:"/courier" } }); return; }
    if (!validate()) return;
    if (payment === "wallet") {
      const sObj = SIZES.find(s => s.type === selected);
      if (walletBal < (sObj?.price || 0)) {
        setErrors(e => ({ ...e, general:"Wallet balance kam hai! ₹" + ((sObj?.price||0) - walletBal) + " aur chahiye." }));
        return;
      }
    }
    setPlacing(true);
    try {
      const res  = await fetch(API + "/courier", {
        method: "POST",
        headers: { "Content-Type":"application/json", Authorization:"Bearer " + getToken() },
        body: JSON.stringify({ parcelType:selected, description:desc, isFragile:fragile, pickupAddress:pickup, deliveryAddress:delivery, paymentMode:payment }),
      });
      const data = await res.json();
      if (data.success) setConfirmed(data.data);
      else setErrors({ general: data.message || "Booking failed!" });
    } catch { setErrors({ general:"Server se connect nahi hua" }); }
    setPlacing(false);
  };

  const doTrack = async () => {
    if (!trackId.trim()) return;
    setTracking_(true); setTrackErr(""); setTracking(null);
    try {
      const res  = await fetch(API + "/courier/track/" + trackId.trim().toUpperCase());
      const data = await res.json();
      if (data.success) setTracking(data.data);
      else setTrackErr(data.message || "Tracking ID nahi mila");
    } catch { setTrackErr("Server error"); }
    setTracking_(false);
  };

  const loadHistory = async () => {
    if (!isLoggedIn) return;
    setLoadHist(true);
    try {
      const res  = await fetch(API + "/courier/my", { headers:{ Authorization:"Bearer " + getToken() } });
      const data = await res.json();
      if (data.success) setHistory(data.couriers || []);
    } catch {}
    setLoadHist(false);
  };

  useEffect(() => { if (step === "history") loadHistory(); }, [step]);

  const fetchWallet = async () => {
    if (!isLoggedIn) return;
    try {
      const res  = await fetch(API + "/wallet/balance", { headers:{ Authorization:"Bearer " + getToken() } });
      const data = await res.json();
      if (data.success) setWalletBal(data.balance || 0);
    } catch {}
  };

  useEffect(() => { fetchWallet(); }, [isLoggedIn, showWallet]);

  if (confirmed) return (
    <BookingConfirmed booking={confirmed} onGoHome={() => navigate("/")}
      onTrack={() => { setConfirmed(null); setTrackId(confirmed.trackingId); setStep("track"); }}/>
  );

  const sizeObj        = SIZES.find(s => s.type === selected);
  const savedAddresses = user?.addresses || [];

  /* ── Tab buttons data ── */
  const TABS = [
    { id:"home",    label:"Home",    icon:HomeIcon  },
    { id:"book",    label:"Book",    icon:Package   },
    { id:"track",   label:"Track",   icon:Search    },
    { id:"history", label:"History", icon:ListOrdered },
  ];

  return (
    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", background:T.bg, minHeight:"100vh", color:T.text, overflowX:"hidden", paddingBottom:140 }}>
      <style>{GLOBAL_CSS}</style>

      {/* ── Header ── */}
      <div style={{ position:"sticky", top:0, zIndex:200, background:"rgba(10,10,15,0.97)", borderBottom:"1px solid " + T.border, padding:"12px 16px", backdropFilter:"blur(20px)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={() => step === "home" ? navigate("/") : setStep("home")}
            style={{ background:T.surface, border:"1px solid " + T.border, borderRadius:10, width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
            <ArrowLeft size={18} color={T.text}/>
          </button>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:"1.05rem", letterSpacing:"-0.3px", color:T.text }}>Send Parcels</div>
            <div style={{ fontSize:"0.62rem", color:T.sub }}>Same-day delivery · Akola</div>
          </div>
          {isLoggedIn && (
            <button onClick={() => setShowWallet(true)}
              style={{ display:"flex", alignItems:"center", gap:7, background:T.greenDim, border:"1px solid " + T.green + "40", borderRadius:100, padding:"7px 13px", cursor:"pointer" }}>
              <Wallet size={13} color={T.green}/>
              <span style={{ fontFamily:"'Syne',sans-serif", fontSize:"0.78rem", fontWeight:800, color:T.green }}>₹{walletBal}</span>
            </button>
          )}
        </div>

        {/* Sub-tabs */}
        <div style={{ display:"flex", gap:6, marginTop:12, overflowX:"auto" }}>
          {TABS.map(t => {
            const active = step === t.id;
            return (
              <button key={t.id} onClick={() => setStep(t.id)}
                style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:100, border:"1px solid " + (active ? T.blue + "60" : T.border), background:active ? T.blueGlow : "transparent", color:active ? T.blue : T.sub, fontWeight:active?700:400, fontSize:"0.75rem", cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap", transition:"all .2s" }}>
                <t.icon size={13}/>
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ━━━━ HOME ━━━━ */}
      {step === "home" && (
        <div style={{ padding:16 }} className="fade-up">

          {/* Hero */}
          <div style={{ background:"linear-gradient(135deg,#060d1f,#0d1e4a,#1a3a8a)", borderRadius:22, padding:"24px 20px", marginBottom:16, position:"relative", overflow:"hidden", border:"1px solid rgba(79,142,247,0.2)" }}>
            <div style={{ position:"absolute", right:-20, bottom:-20, opacity:0.06 }}>
              <Package size={140} color="#fff" strokeWidth={0.8}/>
            </div>
            <div style={{ position:"absolute", top:16, right:16 }}>
              <div style={{ background:"rgba(34,211,122,0.15)", border:"1px solid rgba(34,211,122,0.3)", borderRadius:100, padding:"4px 10px", display:"flex", alignItems:"center", gap:5 }}>
                <div style={{ width:6, height:6, borderRadius:"50%", background:T.green, animation:"pulse 1.5s ease-in-out infinite" }}/>
                <span style={{ fontSize:"0.6rem", fontWeight:700, color:T.green }}>LIVE</span>
              </div>
            </div>
            <div style={{ fontSize:"0.6rem", fontWeight:700, color:"rgba(79,142,247,0.8)", letterSpacing:"1.5px", marginBottom:10, textTransform:"uppercase" }}>Same Day Delivery</div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:"1.6rem", color:T.text, lineHeight:1.2, marginBottom:8 }}>
              Send Parcels<br/>Across Akola
            </div>
            <div style={{ fontSize:"0.78rem", color:"rgba(255,255,255,0.5)", marginBottom:20, lineHeight:1.6 }}>
              Fast, secure, same-day delivery starting at ₹49
            </div>
            <button onClick={() => setStep("book")}
              style={{ background:"linear-gradient(135deg,#1a3a8a,#4f8ef7)", border:"none", borderRadius:12, padding:"12px 24px", color:"#fff", fontWeight:700, fontSize:"0.88rem", cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:8 }}>
              <Send size={15}/> Book a Courier
            </button>
          </div>

          {/* Size cards */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:"0.85rem", color:T.text, marginBottom:12 }}>Parcel Sizes & Prices</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              {SIZES.map(s => (
                <div key={s.type} onClick={() => { setSelected(s.type); setStep("book"); }}
                  style={{ background:T.card, border:"1px solid " + T.border, borderRadius:16, padding:"16px 14px", cursor:"pointer", transition:"all .2s" }}>
                  <div style={{ width:36, height:36, borderRadius:10, background:s.bg, border:"1px solid " + s.color + "30", display:"flex", alignItems:"center", justifyContent:"center", marginBottom:10 }}>
                    <s.Icon size={18} color={s.color} strokeWidth={1.8}/>
                  </div>
                  <div style={{ fontWeight:700, fontSize:"0.83rem", color:T.text, marginBottom:2 }}>{s.label}</div>
                  <div style={{ fontSize:"0.7rem", color:T.sub, marginBottom:8 }}>{s.sub}</div>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:"1rem", color:s.color }}>₹{s.price}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Features */}
          <div style={{ background:T.card, border:"1px solid " + T.border, borderRadius:18, padding:"18px 16px" }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:"0.82rem", color:T.text, marginBottom:14 }}>Why Quickry Courier?</div>
            {[
              { icon:Zap,     color:T.orange, label:"Same-Day Delivery",       sub:"Order before 5 PM"          },
              { icon:Shield,  color:T.blue,   label:"Safe & Insured",           sub:"Fragile items ke liye"      },
              { icon:MapPin,  color:T.green,  label:"Live Tracking",            sub:"Real-time updates"          },
              { icon:Wallet,  color:T.purple, label:"Wallet Payments",          sub:"Easy top-up via UPI"        },
            ].map((f,i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:12, marginBottom:i<3?12:0 }}>
                <div style={{ width:36, height:36, borderRadius:10, background:f.color+"15", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <f.icon size={17} color={f.color} strokeWidth={1.8}/>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:"0.82rem", color:T.text }}>{f.label}</div>
                  <div style={{ fontSize:"0.7rem", color:T.sub }}>{f.sub}</div>
                </div>
                <ChevronRight size={14} color={T.muted}/>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ━━━━ BOOK ━━━━ */}
      {step === "book" && (
        <div style={{ padding:16 }} className="fade-up">

          {/* Parcel Size */}
          <div style={{ background:T.card, border:"1px solid " + T.border, borderRadius:18, padding:"18px", marginBottom:12 }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:"0.85rem", color:T.text, marginBottom:14 }}>Parcel Size Select Karo</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8 }}>
              {SIZES.map(s => {
                const isSel = selected === s.type;
                return (
                  <div key={s.type} onClick={() => setSelected(s.type)}
                    style={{ border:"1.5px solid " + (isSel ? s.color : T.border), borderRadius:12, padding:"10px 6px", textAlign:"center", cursor:"pointer", background:isSel ? s.bg : "transparent", transition:"all .15s" }}>
                    <s.Icon size={20} color={isSel ? s.color : T.muted} strokeWidth={1.8} style={{ margin:"0 auto 5px" }}/>
                    <div style={{ fontSize:"0.62rem", fontWeight:isSel?700:500, color:isSel?s.color:T.sub }}>{s.label}</div>
                    <div style={{ fontSize:"0.68rem", fontWeight:800, color:isSel?s.color:T.text, marginTop:2 }}>₹{s.price}</div>
                  </div>
                );
              })}
            </div>
            {errors.size && <div style={{ color:T.red, fontSize:"0.68rem", marginTop:8, fontWeight:600 }}>{errors.size}</div>}
          </div>

          {/* Pickup */}
          <div style={{ background:T.card, border:"1px solid " + T.border, borderRadius:18, padding:"18px", marginBottom:12 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:T.green }}/>
                <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:"0.85rem", color:T.text }}>Pickup Address</span>
              </div>
              {savedAddresses.length > 0 && (
                <button onClick={() => setShowPickupSheet(true)}
                  style={{ background:T.blueGlow, border:"1px solid " + T.blue + "40", borderRadius:8, padding:"5px 10px", color:T.blue, fontWeight:700, fontSize:"0.68rem", cursor:"pointer", display:"flex", alignItems:"center", gap:5, fontFamily:"inherit" }}>
                  <Edit3 size={10}/> Saved
                </button>
              )}
            </div>
            <AddressForm
              data={pickup}
              onChange={(k,v) => setPickup(p => ({...p,[k]:v}))}
              errors={{ name:errors.pickupName, phone:errors.pickupPhone, address:errors.pickupAddr, pincode:errors.pickupPin, city:errors.pickupCity }}
            />
          </div>

          {/* Arrow divider */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", marginBottom:12, gap:10 }}>
            <div style={{ height:1, flex:1, background:T.border }}/>
            <div style={{ width:32, height:32, borderRadius:"50%", background:T.blueGlow, border:"1px solid " + T.blue + "40", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Truck size={15} color={T.blue}/>
            </div>
            <div style={{ height:1, flex:1, background:T.border }}/>
          </div>

          {/* Delivery */}
          <div style={{ background:T.card, border:"1px solid " + T.border, borderRadius:18, padding:"18px", marginBottom:12 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:T.red }}/>
                <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:"0.85rem", color:T.text }}>Delivery Address</span>
              </div>
              {savedAddresses.length > 0 && (
                <button onClick={() => setShowDeliverySheet(true)}
                  style={{ background:T.blueGlow, border:"1px solid " + T.blue + "40", borderRadius:8, padding:"5px 10px", color:T.blue, fontWeight:700, fontSize:"0.68rem", cursor:"pointer", display:"flex", alignItems:"center", gap:5, fontFamily:"inherit" }}>
                  <Edit3 size={10}/> Saved
                </button>
              )}
            </div>
            <AddressForm
              data={delivery}
              onChange={(k,v) => setDelivery(d => ({...d,[k]:v}))}
              errors={{ name:errors.delivName, phone:errors.delivPhone, address:errors.delivAddr, pincode:errors.delivPin, city:errors.delivCity }}
            />
          </div>

          {/* Parcel Details */}
          <div style={{ background:T.card, border:"1px solid " + T.border, borderRadius:18, padding:"18px", marginBottom:12 }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:"0.82rem", color:T.text, marginBottom:10 }}>Parcel Details</div>
            <input value={desc} onChange={e => setDesc(e.target.value)}
              placeholder="Parcel description (optional)"
              style={{ width:"100%", border:"1.5px solid " + T.border, borderRadius:10, padding:"11px 14px", fontSize:"0.85rem", color:T.text, background:T.surface, marginBottom:12, fontFamily:"inherit" }}/>
            <div onClick={() => setFragile(f => !f)} style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
              <div style={{ width:20, height:20, borderRadius:5, border:"2px solid " + (fragile ? T.red : T.border), background:fragile ? T.red + "22" : "transparent", display:"flex", alignItems:"center", justifyContent:"center", transition:"all .15s" }}>
                {fragile && <CheckCircle size={13} color={T.red}/>}
              </div>
              <span style={{ fontSize:"0.82rem", color:fragile ? T.red : T.sub, fontWeight:fragile?700:400 }}>Fragile item hai</span>
            </div>
          </div>

          {/* Payment */}
          <div style={{ background:T.card, border:"1px solid " + T.border, borderRadius:18, padding:"18px", marginBottom:12 }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:"0.82rem", color:T.text, marginBottom:12 }}>Payment Method</div>
            <div style={{ display:"flex", gap:8 }}>
              {[
                { id:"cod",    label:"Cash on Pickup", icon:Banknote, color:T.orange },
                { id:"wallet", label:"Wallet  ₹" + walletBal, icon:Wallet, color:T.green },
              ].map(p => {
                const active = payment === p.id;
                return (
                  <button key={p.id} onClick={() => setPayment(p.id)}
                    style={{ flex:1, padding:"12px 8px", borderRadius:12, border:"1.5px solid " + (active ? p.color : T.border), background:active ? p.color+"10" : "transparent", color:active ? p.color : T.sub, fontWeight:active?700:400, fontSize:"0.78rem", cursor:"pointer", fontFamily:"inherit", transition:"all .15s", display:"flex", alignItems:"center", justifyContent:"center", gap:7 }}>
                    <p.icon size={14}/>
                    {p.label}
                  </button>
                );
              })}
            </div>
            {payment === "wallet" && walletBal < (sizeObj?.price||0) && (
              <div style={{ marginTop:10, background:T.redDim, border:"1px solid " + T.red + "30", borderRadius:10, padding:"9px 12px", display:"flex", alignItems:"center", gap:8 }}>
                <AlertCircle size={13} color={T.red}/>
                <span style={{ fontSize:"0.73rem", color:T.red }}>Balance kam hai. <button onClick={() => setShowWallet(true)} style={{ background:"none", border:"none", color:T.blue, fontWeight:700, cursor:"pointer", fontSize:"0.73rem", fontFamily:"inherit" }}>Wallet top-up karo</button></span>
              </div>
            )}
          </div>

          {/* Price Summary */}
          {sizeObj && (
            <div style={{ background:"linear-gradient(135deg,#060d1f,#0d1e4a)", border:"1px solid rgba(79,142,247,0.2)", borderRadius:14, padding:"14px 16px", marginBottom:12 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:"0.8rem", color:T.sub }}>Delivery Charge</div>
                  <div style={{ fontSize:"0.68rem", color:T.muted, marginTop:2 }}>{sizeObj.label} · {sizeObj.sub}</div>
                </div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:"1.3rem", color:T.blue }}>₹{sizeObj.price}</div>
              </div>
            </div>
          )}

          {errors.general && (
            <div style={{ background:T.redDim, border:"1px solid " + T.red + "40", borderRadius:10, padding:"10px 14px", marginBottom:12, display:"flex", alignItems:"center", gap:8 }}>
              <AlertCircle size={14} color={T.red}/>
              <span style={{ fontSize:"0.8rem", color:T.red, fontWeight:600 }}>{errors.general}</span>
            </div>
          )}

          <button onClick={placeBooking} disabled={placing}
            style={{ width:"100%", background:placing ? T.surface : "linear-gradient(135deg,#1a3a8a,#4f8ef7)", color:placing ? T.sub : "#fff", border:"1px solid " + (placing ? T.border : "transparent"), borderRadius:16, padding:"15px", fontWeight:700, fontSize:"0.95rem", cursor:placing?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:9, boxShadow:placing?"none":"0 8px 24px rgba(79,142,247,0.3)", transition:"all .2s", fontFamily:"inherit" }}>
            {placing
              ? <><Loader2 size={18} className="spin"/> Booking ho raha hai...</>
              : <><Package size={17}/> Confirm Booking{sizeObj ? " · ₹" + sizeObj.price : ""}</>}
          </button>
        </div>
      )}

      {/* ━━━━ TRACK ━━━━ */}
      {step === "track" && (
        <div style={{ padding:16 }} className="fade-up">
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:"1.1rem", color:T.text, marginBottom:16 }}>Track Your Parcel</div>

          <div style={{ display:"flex", gap:8, marginBottom:16 }}>
            <input value={trackId} onChange={e => setTrackId(e.target.value.toUpperCase())}
              placeholder="Enter Tracking ID (e.g. QC1A2B3C)"
              onKeyDown={e => e.key === "Enter" && doTrack()}
              style={{ flex:1, border:"1.5px solid " + T.border, borderRadius:12, padding:"12px 14px", fontSize:"0.88rem", color:T.text, background:T.card, letterSpacing:"1px", fontFamily:"inherit" }}/>
            <button onClick={doTrack} disabled={tracking_}
              style={{ background:"linear-gradient(135deg,#1a3a8a,#4f8ef7)", border:"none", borderRadius:12, padding:"12px 18px", color:"#fff", fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:"inherit" }}>
              {tracking_ ? <Loader2 size={16} className="spin"/> : <Search size={16}/>}
            </button>
          </div>

          {trackErr && (
            <div style={{ background:T.redDim, border:"1px solid " + T.red + "30", borderRadius:10, padding:"10px 14px", marginBottom:14, color:T.red, fontSize:"0.82rem", fontWeight:600, display:"flex", alignItems:"center", gap:8 }}>
              <AlertCircle size={14}/> {trackErr}
            </div>
          )}

          {tracking && (
            <div className="fade-up">
              <div style={{ background:T.card, borderRadius:18, padding:"18px", marginBottom:12, border:"1px solid " + T.border }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:"0.9rem", color:T.text, letterSpacing:"1px" }}>{tracking.trackingId}</div>
                  <div style={{ background:T.blueGlow, color:T.blue, fontSize:"0.68rem", fontWeight:700, padding:"4px 10px", borderRadius:100, border:"1px solid " + T.blue + "40" }}>
                    {tracking.status?.replace(/_/g," ").toUpperCase()}
                  </div>
                </div>
                {STATUS_STEPS.map((s, i) => {
                  const currentIdx = STATUS_STEPS.map(x => x.status).indexOf(tracking.status);
                  const done = i <= currentIdx;
                  return (
                    <div key={s.status} style={{ display:"flex", alignItems:"flex-start", gap:12, marginBottom:i<STATUS_STEPS.length-1?16:0 }}>
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
                        <div style={{ width:34, height:34, borderRadius:"50%", background:done ? T.blue + "20" : T.surface, border:"2px solid " + (done ? T.blue : T.border), display:"flex", alignItems:"center", justifyContent:"center", transition:"all .3s" }}>
                          <s.Icon size={15} color={done ? T.blue : T.muted} strokeWidth={2}/>
                        </div>
                        {i < STATUS_STEPS.length-1 && <div style={{ width:2, height:16, background:done ? T.blue : T.border, marginTop:2, transition:"all .3s" }}/>}
                      </div>
                      <div style={{ paddingTop:7 }}>
                        <div style={{ fontWeight:done?700:500, fontSize:"0.83rem", color:done?T.text:T.muted }}>{s.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ background:T.card, borderRadius:16, padding:"16px", border:"1px solid " + T.border }}>
                <div style={{ marginBottom:12 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:T.green }}/>
                    <span style={{ fontSize:"0.62rem", fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"1px" }}>Pickup</span>
                  </div>
                  <div style={{ fontSize:"0.83rem", color:T.text, fontWeight:700 }}>{tracking.pickupAddress?.name}</div>
                  <div style={{ fontSize:"0.75rem", color:T.sub }}>{tracking.pickupAddress?.address}</div>
                  <div style={{ fontSize:"0.7rem", color:T.muted }}>{tracking.pickupAddress?.city} {tracking.pickupAddress?.pincode}</div>
                </div>
                <div style={{ height:1, background:T.border, marginBottom:12 }}/>
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:T.red }}/>
                    <span style={{ fontSize:"0.62rem", fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"1px" }}>Delivery</span>
                  </div>
                  <div style={{ fontSize:"0.83rem", color:T.text, fontWeight:700 }}>{tracking.deliveryAddress?.name}</div>
                  <div style={{ fontSize:"0.75rem", color:T.sub }}>{tracking.deliveryAddress?.address}</div>
                  <div style={{ fontSize:"0.7rem", color:T.muted }}>{tracking.deliveryAddress?.city} {tracking.deliveryAddress?.pincode}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ━━━━ HISTORY ━━━━ */}
      {step === "history" && (
        <div style={{ padding:16 }} className="fade-up">
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:"1.1rem", color:T.text }}>My Bookings</div>
            <button onClick={loadHistory} style={{ background:"none", border:"none", cursor:"pointer", color:T.sub, display:"flex", alignItems:"center", gap:5, fontSize:"0.75rem" }}>
              <RefreshCw size={13} style={{ animation:loadHist ? "spin 1s linear infinite" : "none" }}/> Refresh
            </button>
          </div>

          {loadHist ? (
            <div style={{ textAlign:"center", padding:60 }}>
              <Loader2 size={28} color={T.blue} className="spin"/>
            </div>
          ) : history.length === 0 ? (
            <div style={{ textAlign:"center", padding:60 }}>
              <Package size={52} color={T.muted} strokeWidth={1}/>
              <div style={{ fontWeight:700, color:T.sub, marginTop:14, fontSize:"0.88rem" }}>Koi bookings nahi</div>
              <button onClick={() => setStep("book")}
                style={{ marginTop:14, background:"linear-gradient(135deg,#1a3a8a,#4f8ef7)", color:"#fff", border:"none", borderRadius:100, padding:"11px 24px", fontWeight:700, cursor:"pointer", fontSize:"0.85rem", fontFamily:"inherit" }}>
                Book Now
              </button>
            </div>
          ) : history.map(b => {
            const sc = b.status === "delivered" ? T.green : b.status === "cancelled" ? T.red : T.blue;
            return (
              <div key={b._id} style={{ background:T.card, borderRadius:16, padding:"16px", marginBottom:10, border:"1px solid " + T.border }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:"0.88rem", color:T.text, letterSpacing:"1px" }}>{b.trackingId}</div>
                  <div style={{ background:sc+"15", color:sc, fontSize:"0.62rem", fontWeight:800, padding:"3px 10px", borderRadius:100, border:"1px solid " + sc + "30" }}>
                    {b.status?.replace(/_/g," ").toUpperCase()}
                  </div>
                </div>
                <div style={{ fontSize:"0.75rem", color:T.sub, marginBottom:10, display:"flex", alignItems:"center", gap:5 }}>
                  <MapPin size={11} color={T.muted}/> {b.pickupAddress?.city} → {b.deliveryAddress?.city || b.deliveryAddress?.address}
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontSize:"0.7rem", color:T.muted }}>{new Date(b.createdAt).toLocaleDateString("en-IN")}</span>
                  <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:"0.92rem", color:T.blue }}>₹{b.price}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Overlays ── */}
      {showPickupSheet  && <AddressPicker addresses={savedAddresses} onSelect={applyToPickup}   onClose={() => setShowPickupSheet(false)}/>}
      {showDeliverySheet && <AddressPicker addresses={savedAddresses} onSelect={applyToDelivery} onClose={() => setShowDeliverySheet(false)}/>}
      {showWallet && <WalletModal balance={walletBal} onClose={() => setShowWallet(false)} token={getToken()} onBalanceUpdate={fetchWallet}/>}

      {/* ── Bottom Nav ── */}
      <div style={{ position:"fixed", bottom:0, left:0, right:0, background:T.navBg, backdropFilter:"blur(24px)", borderTop:"1px solid " + T.border, display:"flex", zIndex:200, paddingBottom:"env(safe-area-inset-bottom,0px)" }}>
        {NAV.map(({ icon:Icon, label, route }) => {
          const active = route === "/courier";
          return (
            <button key={route} onClick={() => navigate(route)}
              style={{ flex:1, background:"none", border:"none", padding:"10px 0 8px", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:3, position:"relative" }}>
              <Icon size={21} color={active ? T.blue : T.muted} strokeWidth={active?2.3:1.8}/>
              <span style={{ fontSize:"0.58rem", fontWeight:active?800:400, color:active?T.blue:T.muted }}>{label}</span>
              {active && <div style={{ position:"absolute", bottom:0, width:20, height:2.5, background:T.blue, borderRadius:100 }}/>}
            </button>
          );
        })}
      </div>
    </div>
  );
}